import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  ImageList,
  ImageListItem,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  Category,
  AttachMoney,
  Star,
  LocationOn,
  Language,
  Map,
  Directions,
  Share,
} from "@mui/icons-material";
import axios from "axios";
import { Place, PlaceType, Price } from "../Place";
import config from "../config";
import { useParams } from "react-router-dom";
import FavoriteButton from "./FavoriteButton";
import ImageModal from "./ImageModal";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface PlaceDetailProps {
  id?: string;
  onClose?: () => void;
}

const PlaceDetail: React.FC<PlaceDetailProps> = ({ id: propId, onClose }) => {
  const { slug: slugSt, id: urlId } = useParams<{ slug: string; id: string }>();
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [startIndex, setStartIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const { pathname } = useLocation();

  const imagesPerPage = 1;
  const id = propId || urlId;

  // SEO Meta Data - Mejorado con más keywords
  const getSeoTitle = () => 
    selectedPlace 
      ? `${selectedPlace.name} en ${selectedPlace.direccion.split(',')[0]} | Lugares para niños en Buenos Aires - Chicos X Baires`
      : "Lugares para niños en Buenos Aires | Chicos X Baires";

  const getSeoDescription = () => {
    if (!selectedPlace) return "Descubre los mejores lugares para niños en Buenos Aires";
    
    const baseDesc = `Visita ${selectedPlace.name} . `;
    const typeDesc = `Actividad ${selectedPlace.placeType} `;
    const priceDesc = selectedPlace.price === Price.Gratis 
      ? "totalmente gratuita" 
      : ` precio ${selectedPlace.price}`;
    
    return baseDesc + typeDesc  + ". " + 
      (selectedPlace.description?.substring(0, 100) || "Perfecto para disfrutar en familia.");
  };

  const canonicalUrl = `https://chicosxbaires.com.ar${pathname}`;
  const seoImage = selectedPlace?.fotos?.[0]
    ? `${config.backendUrl}${selectedPlace.fotos[0]}?w=1200&h=630&fit=crop`
    : `${config.backendUrl}/default-place-image.jpg`;

  const getGoogleMapsDirectionsUrl = () => {
    if (!selectedPlace) return "";
    return `https://www.google.com/maps/dir/?api=1&destination=${selectedPlace.lat},${selectedPlace.longi}&travelmode=driving`;
  };

  useEffect(() => {
    if (id) {
      axios
        .get<Place>(`${config.backendUrl}/api/Places/${id}`)
        .then((response) => {
          setSelectedPlace(response.data);
          // Preload images for better performance
          response.data.fotos?.forEach(foto => {
            const img = new Image();
            img.src = `${config.backendUrl}${foto}`;
          });
        })
        .catch(console.error);
    }
  }, [id]);

  const handleNext = () => {
    setStartIndex((prevIndex) => {
      const nextIndex = prevIndex + imagesPerPage;
      const maxIndex = (selectedPlace?.fotos?.length || 0) - imagesPerPage;
      return Math.min(nextIndex, maxIndex);
    });
  };

  const handlePrev = () => {
    setStartIndex((prevIndex) => Math.max(prevIndex - imagesPerPage, 0));
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setModalOpen(true);
  };

  const handleCloseModal = () => setModalOpen(false);

  if (!selectedPlace) {
    return (
      <>
        <Helmet>
          <title>Lugares para niños en Buenos Aires | Chicos X Baires</title>
          <meta name="description" content="Descubre los mejores lugares para niños en Buenos Aires" />
        </Helmet>
        <Typography>Cargando información del lugar...</Typography>
      </>
    );
  }
  const categoryMapping = {
    Plaza: 'Plaza',
    ParqueTematico: 'Parque Temático',
    Campo: 'Campo',
    JuegosNiños: 'Juegos para Niños',
    CamasElasticas: 'Camas Elásticas',
    ParqueAcuatico: 'Parque Acuático',
    Museo: 'Museo',
    Granja: 'Granja',
    ParqueNatural: 'Parque Natural',
    EscapeRooms: 'Escape Rooms',
    Restaurante: 'Restaurante',
    Zoologicos: 'Zoológicos',
    Cine: 'Cine',
    Arcade: 'Arcade',
    Shopping: 'Shopping',
  };
  // Generate breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": "https://chicosxbaires.com.ar"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": selectedPlace.name,
        "item": canonicalUrl
      }
    ]
  };

  // Generate place schema
  const placeSchema = {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": selectedPlace.name,
    "description": getSeoDescription(),
    "url": canonicalUrl,
    "image": seoImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": selectedPlace.direccion,
      "addressLocality": "Buenos Aires",
      "addressCountry": "AR"
    },
    "openingHoursSpecification": selectedPlace.horarios?.length ? {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ],
      "opens": "10:00",
      "closes": "18:00"
    } : undefined,
    "priceRange": selectedPlace.price === Price.Gratis ? "Gratis" : 
                 selectedPlace.price === Price.Economico ? "$" :
                 selectedPlace.price === Price.Moderado ? "$$" : "$$$"
  };

  return (
    <>
      <Helmet>
        <title>{getSeoTitle()}</title>
        <meta name="description" content={getSeoDescription()} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="place" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={getSeoTitle()} />
        <meta property="og:description" content={getSeoDescription()} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="Chicos X Baires" />
        <meta property="place:location:latitude" content={selectedPlace.lat?.toString()} />
        <meta property="place:location:longitude" content={selectedPlace.longi?.toString()} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getSeoTitle()} />
        <meta name="twitter:description" content={getSeoDescription()} />
        <meta name="twitter:image" content={seoImage} />
        
        {/* Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(placeSchema)}
        </script>
      </Helmet>

      <Box component="article" sx={{ padding: 2 }}>
        <Card sx={{ paddingBottom: 5 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h1" component="h1" sx={{ fontSize: '2rem', fontWeight: 'bold' }}>
                {selectedPlace.name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FavoriteButton placeId={selectedPlace.id} />
                {onClose && (
                  <IconButton onClick={onClose} size="small" aria-label="Cerrar detalles">
                    <Close />
                  </IconButton>
                )}
              </Box>
            </Box>
            
            {selectedPlace.fotos?.length > 0 && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  width="100%"
                  sx={{     px: { xs: 0, sm: 16 }, // xs (mobile): 0, sm (tablet+): 16
                }}
              >
                <IconButton
                  onClick={handlePrev}
                  disabled={startIndex === 0}
                  size="small"
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>

                <Box flex={1}>
                  <ImageList cols={imagesPerPage} gap={8}>
                    {selectedPlace.fotos
                      .slice(startIndex, startIndex + imagesPerPage)
                      .map((item, index) => (
                        <ImageListItem
                          key={index}
                          onClick={() =>
                            handleImageClick(`${config.backendUrl}${item}`)
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          <img
                            src={`${config.backendUrl}${item}?w=300&h=150&fit=crop&auto=format`}
                            srcSet={`${config.backendUrl}${item}?w=300&h=150&fit=crop&auto=format&dpr=2 2x`}
                            loading="lazy"
                            alt={`${selectedPlace.name} - Imagen ${index + 1}`}
                            title={`Fotos de ${selectedPlace.name}`}
                          />
                        </ImageListItem>
                      ))}
                  </ImageList>
                </Box>

                <IconButton
                  onClick={handleNext}
                  disabled={
                    startIndex + imagesPerPage >= selectedPlace.fotos.length
                  }
                  size="small"
                  aria-label="Siguiente imagen"
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}
            
            <Typography
              variant="body1"
              component="div"
              sx={{
                color: "text.secondary",
                mt: 2,
                whiteSpace: "break-spaces",
              }}
              dangerouslySetInnerHTML={{ __html: selectedPlace.description.replace(/\n/g, '<br />') }}
            />

            <Box component="section" sx={{ mt: 4 }}>
              <Typography variant="h2" component="h2" sx={{ fontSize: '1.5rem', fontWeight: 'bold', mb: 2 }}>
                Información del lugar
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  width: "100%",
                }}
              >
                <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Category sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography variant="body1">
                      <strong>Tipo:</strong> {categoryMapping[selectedPlace.placeType?.toString() as keyof typeof categoryMapping]}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <AttachMoney sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography variant="body1">
                      <strong>Precio:</strong> {selectedPlace.price}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <LocationOn sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography variant="body1">
                      <strong>Dirección:</strong> {selectedPlace.direccion}
                    </Typography>
                  </Box>                 
                 
                </Box>
              </Box>

              {selectedPlace.horarios?.length > 0 && (
                <Box component="section" sx={{ mt: 3 }}>
                  <Typography variant="h3" component="h3" sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 1 }}>
                    Horarios
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {selectedPlace.horarios.map((horario, index) => (
                      <li key={index}>
                        <Typography variant="body1">{horario}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Box>
          </CardContent>

          <CardActions sx={{ flexWrap: 'wrap' }}>
            {selectedPlace.webSite && (
              <Button
                size="small"
                href={selectedPlace.webSite}
                target="_blank"
                rel="noopener noreferrer nofollow"
                startIcon={<Language />}
                aria-label={`Sitio web de ${selectedPlace.name}`}
              >
                Sitio web
              </Button>
            )}

            {selectedPlace.urlGoogle && (
              <Button
                size="small"
                href={selectedPlace.urlGoogle}
                target="_blank"
                rel="noopener noreferrer nofollow"
                startIcon={<Map />}
                aria-label={`Ver ${selectedPlace.name} en Google Maps`}
              >
                Google Maps
              </Button>
            )}

            <Button
              size="small"
              href={getGoogleMapsDirectionsUrl()}
              target="_blank"
              rel="noopener noreferrer nofollow"
              startIcon={<Directions />}
              aria-label={`Cómo llegar a ${selectedPlace.name}`}
            >
              Cómo llegar
            </Button>

            <Button
              size="small"
              onClick={() => {
                const shareUrl = `${window.location.origin}/lugar/c/${selectedPlace.id}`;
                if (navigator.share) {
                  navigator
                    .share({
                      title: `${selectedPlace.name} - Chicos X Baires`,
                      text: `Mira este lugar en Chicos X Baires:  ${selectedPlace.name}.`,
                      url: shareUrl,
                    })
                    .catch(console.error);
                } else {
                  navigator.clipboard
                    .writeText(shareUrl)
                    .then(() => {
                      alert("Enlace copiado al portapeles");
                    })
                    .catch(console.error);
                }
              }}
              startIcon={<Share />}
              aria-label={`Compartir información de ${selectedPlace.name}`}
            >
              Compartir
            </Button>
          </CardActions>
        </Card>

        <ImageModal
          open={modalOpen}
          handleClose={handleCloseModal}
          imageUrl={selectedImage}
        />
      </Box>
    </>
  );
};

export default PlaceDetail;